import { IAlbum } from 'ngx-lightbox';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import DateHelper from 'src/app/helpers/date-helper';
import { IPBoardHelper } from 'src/app/helpers/ip-board-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { UtilityFunctions } from 'src/app/helpers/utility-functions';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import Product from 'src/app/model/product/product';
import { BaseProject } from 'src/app/model/project/base-project';
import { Project } from 'src/app/model/project/project';
import {
  SubProject,
  SUB_PROJECT_TYPES
} from 'src/app/model/project/sub-project';
import { ClientCornerMapHelper } from '../../../helpers/client-corner-map-helper';

export class ClientCornerProjectInfoHelper {
  private constructor() {}

  static getLightboxAlbum(project: Project, subProject: SubProject): IAlbum[] {
    return subProject.type === 'BaseProject'
      ? this.getBaseProjectLightboxAlbum(project)
      : this.getAdjustmentLightboxAlbum(subProject);
  }

  static getBaseProjectLightboxAlbum(project: Project): IAlbum[] {
    const toReturn: IAlbum[] = [];
    project.products.forEach(p => {
      if (CollectionHelper.isPopulated(p.productAssets)) {
        p.productAssets.forEach(pa => {
          if (!pa.archived && pa.formSection === 'execution') {
            toReturn.push({
              src: `/api/assets/${pa.asset.uuid}`,
              thumb: `/api/assets/${pa.asset.uuid}`
            });
          }
        });
      }
    });
    return toReturn;
  }

  static getAdjustmentLightboxAlbum(subProject: SubProject): IAlbum[] {
    const toReturn: IAlbum[] = [];
    subProject.projectPlacement.projectPlacementAssets.forEach(pa => {
      if (!pa.archived && pa.formSection === 'adjustmentPlacementSuccess') {
        toReturn.push({
          src: `/api/assets/${pa.asset.uuid}`,
          thumb: `/api/assets/${pa.asset.uuid}`
        });
      }
    });
    return toReturn;
  }

  // return[0]: canRequestAdjustment, return[1]: canRequestSoldRented
  static calculateCanRequestAdjustmentAndSoldRented(
    project: Project
  ): boolean[] {
    if (!CollectionHelper.isPopulated(project.subProjects)) {
      return [false, false];
    }
    const toReturn = [true, true];
    for (const subProject of project.subProjects) {
      toReturn[0] =
        toReturn[0] &&
        ProjectHelper.EXECUTED_FOR_CLIENT_STATUSES.includes(subProject.status);
      toReturn[1] =
        toReturn[0] && toReturn[1] && subProject.type !== 'SoldRented';
      if (!(toReturn[0] || toReturn[1])) {
        break;
      }
    }
    return toReturn;
  }

  // return[0]: adjustmentActionLabel, return[1]: soldRentedActionLabel, return[2]: repoActionLabel
  static calculateActionButtonLabels(project: Project): string[] {
    const toReturn = [
      'Aanpassing aanvragen',
      'Verkocht/Verhuurd aanvragen',
      'Weghalen aanvragen'
    ];

    if (!CollectionHelper.isPopulated(project.subProjects)) {
      return toReturn;
    }

    let mostRecentAdjustment: SubProject = null;
    let mostRecentSoldRented: SubProject = null;
    let mostRecentRepo: SubProject = null;

    for (const subProject of project.subProjects) {
      switch (subProject.type) {
        case 'Adjustment':
          mostRecentAdjustment =
            mostRecentAdjustment == null
              ? subProject
              : UtilityFunctions.max(
                  mostRecentAdjustment,
                  subProject,
                  ClientCornerMapHelper.compareSubProjectRecency
                );
          break;
        case 'SoldRented':
          mostRecentSoldRented =
            mostRecentSoldRented == null
              ? subProject
              : UtilityFunctions.max(
                  mostRecentSoldRented,
                  subProject,
                  ClientCornerMapHelper.compareSubProjectRecency
                );
          break;
        case 'Repo':
          mostRecentRepo =
            mostRecentRepo == null
              ? subProject
              : UtilityFunctions.max(
                  mostRecentRepo,
                  subProject,
                  ClientCornerMapHelper.compareSubProjectRecency
                );
          break;
        default:
      }
    }

    toReturn[0] =
      mostRecentAdjustment != null
        ? `Aangevraagd ${DateHelper.formatDate(
            mostRecentAdjustment.currentCreatedAt
          )}`
        : toReturn[0];
    toReturn[1] =
      mostRecentSoldRented != null
        ? `Aangevraagd ${DateHelper.formatDate(
            mostRecentSoldRented.currentCreatedAt
          )}`
        : toReturn[1];
    toReturn[2] =
      mostRecentRepo != null
        ? `Aangevraagd ${DateHelper.formatDate(
            mostRecentRepo.currentCreatedAt
          )}`
        : toReturn[2];

    return toReturn;
  }

  static getProductTitle(product: Product): string {
    const toReturn = ProductHelper.isIPBoard(product)
      ? this.getIPBoardTitle(<IPBoard>product)
      : product.title;
    return toReturn == null ? null : toReturn.toUpperCase();
  }

  private static getIPBoardTitle(ipBoard: IPBoard): string {
    if (ipBoard.boardPart == null) {
      return null;
    }
    if (ipBoard.boardPart.otherTitle != null) {
      return ipBoard.boardPart.otherTitle;
    }
    if (ipBoard.boardPart.titleAdjustment != null) {
      return ipBoard.boardPart.titleAdjustment.title;
    }
    return ipBoard.boardPart.sign != null ? ipBoard.boardPart.sign.title : null;
  }

  static getDateDisplay(subProject: SubProject, project: Project): string {
    return subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT
      ? this.getProductDateDisplay(
          CollectionHelper.isPopulated(project.products)
            ? project.products[0]
            : null,
          <BaseProject>subProject
        )
      : this.getAdjustmentDateDisplay(subProject);
  }

  static getProductDateDisplay(
    product: Product,
    baseProject: BaseProject
  ): string {
    if (product == null || product.productPlacement == null) {
      return null;
    }
    if (
      product.productPlacement.executionComplete &&
      product.productPlacement.executionDate != null
    ) {
      return DateHelper.formatDate(product.productPlacement.executionDate);
    } else {
      if (ProductHelper.isIPBoard(product)) {
        if (
          baseProject != null &&
          baseProject.tourItem != null &&
          baseProject.tourItem.tour != null &&
          baseProject.tourItem.tour.date != null
        ) {
          return DateHelper.formatDate(baseProject.tourItem.tour.date);
        }
      }
      return DateHelper.formatDate(
        product.productPlacement.actualPlacementDate
      );
    }
  }

  private static getAdjustmentDateDisplay(subProject: SubProject): string {
    if (subProject.projectPlacement == null) {
      return null;
    }
    if (
      subProject.projectPlacement.placementDone &&
      subProject.currentExecutionDate != null
    ) {
      return DateHelper.formatDate(subProject.currentExecutionDate);
    } else {
      if (
        subProject.tourItem != null &&
        subProject.tourItem.tour != null &&
        subProject.tourItem.tour.date != null
      ) {
        return DateHelper.formatDate(subProject.tourItem.tour.date);
      }
      return DateHelper.formatDate(subProject.currentConfirmedPlacementDate);
    }
  }

  static getProductTypeDisplays(products: Product[]): string[] {
    if (!CollectionHelper.isPopulated(products)) {
      return [];
    }

    const result: string[] = [];

    products.forEach(p => {
      let typeDisplay: string = null;

      if (ProductHelper.isIPBoard(p)) {
        typeDisplay = this.getTypeDisplayForIPBoard(p);
      }

      if (!StringHelper.isEmptyOrWhiteSpace(typeDisplay)) {
        result.push(typeDisplay);
      }
    });

    return result;
  }

  private static getTypeDisplayForIPBoard(ipBoard: IPBoard): string {
    if (ipBoard.boardPart != null) {
      const boardTypeDisplay =
        ipBoard.boardPart.boardType == null
          ? null
          : IPBoardHelper.isDivergentBoard(ipBoard.boardPart)
          ? ipBoard.boardPart.divergentBoardType
          : ipBoard.boardPart.boardType.label;
      return StringHelper.join(
        [boardTypeDisplay, IPBoardHelper.getPlacementType(ipBoard.boardPart)],
        ' '
      );
    }

    return null;
  }
}
